<template>
  <div class="push-paid-ads-container">
    <!-- Header Section -->
    <div class="header-section">
      <div class="badge-title">
        <span class="admin-badge">Admin Only</span>
        <h1>Network Ads</h1>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="tab-navigation">
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'campaigns' }"
        @click="activeTab = 'campaigns'"
      >
        <span class="tab-icon"><i class="el-icon-s-marketing"></i></span>
        Ad Campaigns
      </button>
      <button 
        class="tab-button" 
        :class="{ active: activeTab === 'gallery' }"
        @click="activeTab = 'gallery'"
      >
        <span class="tab-icon"><i class="el-icon-picture"></i></span>
        Network Ads Gallery
      </button>
    </div>

    <!-- Tab Content: Ad Campaigns -->
    <div v-if="activeTab === 'campaigns'" class="tab-content">
      <!-- Search and Action Bar -->
      <div class="search-action-bar">
        <div class="search-container">
          <input 
            type="text" 
            placeholder="Search pushed ads..." 
            class="search-input"
            v-model="searchQuery"
          >
          <button class="search-button">
            <i class="el-icon-search"></i>
            Search
          </button>
        </div>
        <button class="push-new-ad-button" @click="openPushAdModal">
          <i class="el-icon-circle-plus"></i>
          Push New Ad
        </button>
      </div>

      <!-- Info Box -->
      <div class="info-box">
        <div class="info-header">
          <i class="el-icon-info info-icon"></i>
          <span class="info-title">Network Ads</span>
        </div>
        <p class="info-description" v-if="isAdmin">
          Ads pushed from this panel will appear on user machines as "Network Ads" that cannot be edited or deleted by users. They will be displayed alongside the user's own ad configurations.
        </p>
        <p class="info-description" v-else>
          Ads pushed from the VTM Network will appear in this space. They cannot be edited or deleted and will be displayed alongside user-created ads.
        </p>
      </div>

      <!-- Table Section -->
      <div class="table-container">
        <table class="ads-table">
          <thead>
            <tr>
              <th>Preview</th>
              <th>Ad Title</th>
              <th>Company</th>
              <th>Type</th>
              <th>Target</th>
              <th>Date Range</th>
              <th>Pushed By</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ad in filteredAds" :key="ad.id" class="table-row">
              <td class="preview-cell">
                <template v-if="ad.type === 1">
                  <img v-if="campaignVideoThumbnail[ad.adId || ad.id]" :src="campaignVideoThumbnail[ad.adId || ad.id]" :alt="ad.head" class="preview-image">
                  <video
                    v-else
                    :src="ad.url"
                    class="preview-image campaign-preview-video"
                    autoplay
                    muted
                    loop
                    playsinline
                  ></video>
                </template>
                <img v-else :src="ad.url || ad.thum" :alt="ad.head" class="preview-image">
              </td>
              <td><strong>{{ ad.head }}</strong></td>
              <td>{{ ad.advertiser }}</td>
              <td>
                <span class="type-badge" :class="(ad.display === 1 ? 'screen' : 'banner').toLowerCase()">
                  {{ ad.display === 1 ? 'Screen' : 'Banner' }}
                </span>
              </td>
              <td>
                <span class="view-badge view-clickable" @click="openTargetAssignments(ad)">
                  <i class="el-icon-view eye-icon"></i>
                  View
                </span>
              </td>
              <td class="date-range-cell">
                <span v-if="ad.startTime || ad.endTime">
                  <span>{{ ad.startTime ? ad.startTime.slice(0, 10) : '—' }}</span>
                  <span class="date-arrow"> → </span>
                  <span>{{ ad.endTime ? ad.endTime.slice(0, 10) : '—' }}</span>
                </span>
                <span v-else class="no-date">{{ ad.createTime }}</span>
              </td>
              <td>{{ ad.pushedBy || '—' }}</td>
              <td>
                <span class="vtm-pill" :class="getAdStatus(ad) === 'active' ? 'vtm-pill--ok' : 'vtm-pill--off'">
                  {{ getAdStatus(ad) === 'active' ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="actions-cell">
                <button class="action-btn edit-btn" @click="openEditAdModal(ad)"><i class="el-icon-edit"></i> Edit</button>
                <button class="action-btn delete-btn" @click="deleteAd(ad)"><i class="el-icon-delete"></i> Remove</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination">
  <span class="total-count">Total {{ activeTab === 'campaigns' ? filteredAds.length : galleryItems.length }}</span>
        <div class="pagination-controls">
          <button class="pagination-btn">‹</button>
          <button class="pagination-btn active">1</button>
          <button class="pagination-btn">›</button>
        </div>
      </div>
    </div>

    <!-- Tab Content: Paid Ads Gallery -->
    <div v-if="activeTab === 'gallery'" class="tab-content">
      <!-- Filter and Upload Bar -->
      <div class="gallery-header">
        <div class="filter-search">
          <input 
            v-model="gallerySearchQuery"
            type="text" 
            placeholder="Search gallery..." 
            class="search-input"
            style="max-width: 20%;"
          >
          <div class="filter-buttons">
            <button class="filter-btn" :class="{ active: activeFilter === 'all' }" @click="activeFilter = 'all'">All</button>
            <button class="filter-btn" :class="{ active: activeFilter === 'screen' }" @click="activeFilter = 'screen'">
              <i class="el-icon-monitor"></i>
              Screen
            </button>
            <button class="filter-btn" :class="{ active: activeFilter === 'banner' }" @click="activeFilter = 'banner'">
              <i class="el-icon-picture"></i>
              Banner
            </button>
          </div>
        </div>
        <button class="upload-ad-button" @click="openUploadAdModal">
          <i class="el-icon-upload"></i>
          Upload Ad
        </button>
      </div>

      <!-- Gallery Grid -->
      <div class="gallery-grid">
        <div v-for="item in filteredGalleryItems" :key="item.id" class="gallery-card">
          <div class="gallery-image-container">
            <img v-if="item.type === 0" :src="item.image" :alt="item.title" class="gallery-image">
            <video
              v-else-if="item.type === 1"
              :src="item.image"
              class="gallery-image gallery-video"
              autoplay
              muted
              loop
              playsinline
            ></video>
            <span class="gallery-type-label">{{ item.display === 1 ? 'Screen' : 'Banner' }}</span>
            <span class="gallery-format-label"><i class="el-icon-picture"></i> {{ item.type === 1 ? 'Video' : 'Image' }}</span>
          </div>
          <div class="gallery-info">
            <h3>{{ item.title }}</h3>
            <p>{{ item.company }}</p>
            <div class="gallery-footer">
              <span class="gallery-date">{{ item.date }}</span>
              <button class="delete-gallery-btn" @click="deleteGalleryItem(item)"><i class="el-icon-delete"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Push Ad Modal -->
    <div v-if="showPushAdModal" class="modal-overlay" @click.self="closePushAdModal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title">
            <i class="el-icon-money modal-icon"></i>
            <span>Push Network Ad</span>
          </div>
          <button class="modal-close-btn" @click="closePushAdModal">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Select Advertisements (multi-select) -->
          <div class="form-group">
            <label class="form-label">Select Advertisement(s) <span class="required-hint">(min. 1 required)</span></label>
            <div class="custom-dropdown">
              <button 
                class="dropdown-toggle" 
                @click="showAdvertisementDropdown = !showAdvertisementDropdown"
                type="button"
              >
                <span v-if="pushSelectedAdCount > 0" class="selected-item">
                  <span class="selected-count">{{ pushSelectedAdCount }} ad(s) selected</span>
                </span>
                <span v-else class="placeholder-text">
                  Choose ad(s) from the Paid Ads gallery...
                </span>
                <i class="el-icon-arrow-down dropdown-arrow"></i>
              </button>

              <!-- Dropdown List with Checkboxes -->
              <div v-if="showAdvertisementDropdown" class="dropdown-list dropdown-list-multiselect">
                <div 
                  v-for="item in galleryItems" 
                  :key="item.id" 
                  class="dropdown-item"
                  :class="{ active: isPushAdSelected(item) }"
                  @click="togglePushAdSelection(item)"
                >
                  <input
                    type="checkbox"
                    class="dropdown-item-checkbox"
                    :checked="isPushAdSelected(item)"
                    @click.stop="togglePushAdSelection(item)"
                  >
                  <img v-if="item.type === 0" :src="item.image" :alt="item.title" class="dropdown-item-thumbnail">
                  <video
                    v-else-if="item.type === 1"
                    :src="item.image"
                    class="dropdown-item-thumbnail dropdown-item-thumbnail-video"
                    muted
                    loop
                    playsinline
                    preload="metadata"
                  ></video>
                  <div class="dropdown-item-content">
                    <div class="dropdown-item-title">{{ item.title }}</div>
                    <div class="dropdown-item-company">({{ item.company }})</div>
                  </div>
                  <span class="dropdown-item-badge" :class="item.display === 2 ? 'banner' : 'screen'">
                    {{ item.display === 2 ? 'Banner' : 'Screen' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Date Range -->
          <div class="date-range-group">
            <div class="form-group" style="flex: 1;">
              <label class="form-label">Start Date & Time – End Date & Time</label>
              <el-date-picker
                v-model="pushDateRange"
                type="datetimerange"
                range-separator="-"
                :start-placeholder="$t('searchOrder.start')"
                :end-placeholder="$t('searchOrder.end')"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                class="network-datetime-picker network-datetime-range-picker"
                style="width: 100%;"
                clearable
              />
            </div>
          </div>

          <!-- Target Options -->
          <div class="form-group">
            <label class="form-label">Target</label>
            <div class="target-options">
              <button 
                class="target-btn" 
                :class="{ active: targetOption === 'all' }"
                @click="targetOption = 'all'"
              >
                All Machines
              </button>
              <button 
                class="target-btn" 
                :class="{ active: targetOption === 'groups' }"
                @click="targetOption = 'groups'"
              >
                Machine Groups
              </button>
              <button 
                class="target-btn" 
                :class="{ active: targetOption === 'individual' }"
                @click="targetOption = 'individual'"
              >
                Individual Machines
              </button>
            </div>

            <!-- Machine Selection (for Individual Machines) -->
            <div v-if="targetOption === 'individual'" class="machine-selection">
              <div class="machines-header">
                <input 
                  v-model="machineSearchQuery" 
                  type="text" 
                  placeholder="Search machines..." 
                  class="search-machines"
                >
                <span class="machines-selected">{{ modalMachineList.filter(m => m.selected).length }} selected</span>
              </div>
              <div 
                class="machines-items machines-items-scroll"
                @scroll="onMachineListScroll"
              >
                <div 
                  v-for="machine in modalMachinesFiltered" 
                  :key="machine.id"
                  class="machine-item"
                >
                  <input 
                    type="checkbox" 
                    :id="'machine-' + machine.id"
                    v-model="machine.selected"
                    class="machine-checkbox"
                  />
                  <label :for="'machine-' + machine.id" class="machine-label">
                    <div class="machine-info">
                      <span class="machine-id">{{ machine.equipmentName ? machine.equipmentName + ' (' + (machine.equipmentNo || '') + ')' : (machine.equipmentNo || 'N/A') }}</span>
                      <span class="machine-address">{{ machine.address }}</span>
                    </div>
                    <span class="machine-group">{{ machine.group }}</span>
                  </label>
                </div>
                <div v-if="machineListLoading" class="machines-loading">Loading...</div>
                <div v-else-if="machineListBackgroundLoading && modalMachineList.length === 0" class="machines-loading">Loading equipment...</div>
              </div>
            </div>

            <!-- Machine Groups Tab -->
            <div v-if="targetOption === 'groups'" class="groups-list">
              <div class="search-box">
                <input 
                  type="text" 
                  placeholder="Search groups..." 
                  v-model="groupSearchQuery"
                  class="search-input-modal"
                />
              </div>
              <div class="groups-items">
                <div 
                  v-for="group in filteredMachineGroups" 
                  :key="group.id"
                  class="group-item"
                >
                  <input 
                    type="checkbox" 
                    :id="'group-' + group.id"
                    v-model="group.selected"
                    class="group-checkbox"
                  />
                  <label :for="'group-' + group.id" class="group-label">
                    <div class="group-info">
                      <span class="group-name-text">{{ group.name }}</span>
                      <span class="group-date">Created {{ group.createdDate }}</span>
                    </div>
                    <span class="group-count">{{ group.machineCount }} machines</span>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="targetOption === 'all'" class="target-description">
              This ad will be pushed to all machines in the network
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="closePushAdModal">Cancel</button>
          <button class="btn-push-ad" @click="pushAd">Push Ad</button>
        </div>
      </div>
    </div>

    <!-- Edit Ad Modal -->
    <div v-if="showEditAdModal" class="modal-overlay" @click.self="closeEditAdModal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title">
            <i class="el-icon-edit modal-icon"></i>
            <span>Edit Paid Ad</span>
          </div>
          <button class="modal-close-btn" @click="closeEditAdModal">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Title and Company -->
          <div class="edit-form-row">
            <div class="form-group">
              <label class="form-label">Title</label>
              <input 
                v-model="editFormData.title" 
                type="text" 
                class="form-input"
              >
            </div>
            <div class="form-group">
              <label class="form-label">Company</label>
              <input 
                v-model="editFormData.company" 
                type="text" 
                class="form-input"
              >
            </div>
          </div>

          <!-- Date Range -->
          <div class="date-range-group">
            <div class="form-group" style="flex: 1;">
              <label class="form-label">Start Date & Time – End Date & Time</label>
              <el-date-picker
                v-model="editFormData.dateRange"
                type="datetimerange"
                range-separator="-"
                :start-placeholder="$t('searchOrder.start')"
                :end-placeholder="$t('searchOrder.end')"
                value-format="yyyy-MM-dd HH:mm:ss"
                format="yyyy-MM-dd HH:mm:ss"
                :default-time="['00:00:00', '23:59:59']"
                class="network-datetime-picker network-datetime-range-picker"
                style="width: 100%;"
                clearable
              />
            </div>
          </div>

          <!-- Target Options -->
          <div class="form-group">
            <label class="form-label" style="margin-top:10px">Target</label>
            <div class="target-options">
              <button 
                class="target-btn" 
                :class="{ active: editTargetOption === 'all' }"
                @click="editTargetOption = 'all'"
              >
                All Machines
              </button>
              <button 
                class="target-btn" 
                :class="{ active: editTargetOption === 'groups' }"
                @click="editTargetOption = 'groups'"
              >
                Machine Groups
              </button>
              <button 
                class="target-btn" 
                :class="{ active: editTargetOption === 'individual' }"
                @click="editTargetOption = 'individual'"
              >
                Individual Machines
              </button>
            </div>

            <!-- Machine Selection (for Individual Machines) -->
            <div v-if="editTargetOption === 'individual'" class="machine-selection">
              <div class="machines-header">
                <input 
                  v-model="machineSearchQuery" 
                  type="text" 
                  placeholder="Search machines..." 
                  class="search-machines"
                >
                <span class="machines-selected">{{ editFormData.selectedMachines.length }} selected</span>
              </div>

              <div 
                class="machines-list machines-items-scroll"
                @scroll="onMachineListScroll"
              >
                <div 
                  v-for="machine in modalMachinesFiltered" 
                  :key="machine.id" 
                  class="machine-item"
                >
                  <input 
                    type="checkbox" 
                    :id="'edit-machine-' + machine.id" 
                    :value="machine.id"
                    v-model="editFormData.selectedMachines"
                    class="machine-checkbox"
                  >
                  <label :for="'edit-machine-' + machine.id" class="machine-label">
                    <div class="machine-info">
                      <span class="machine-id">{{ machine.equipmentName ? machine.equipmentName + ' (' + (machine.equipmentNo || '') + ')' : (machine.equipmentNo || 'N/A') }}</span>
                      <span class="machine-address">{{ machine.address }}</span>
                    </div>
                    <span class="machine-group">{{ machine.group }}</span>
                  </label>
                </div>
                <div v-if="machineListLoading" class="machines-loading">Loading...</div>
                <div v-else-if="machineListBackgroundLoading && modalMachineList.length === 0" class="machines-loading">Loading equipment...</div>
              </div>
            </div>

            <!-- Group Selection (for Machine Groups) -->
            <div v-if="editTargetOption === 'groups'" class="groups-list">
              <div class="search-box">
                <input 
                  type="text" 
                  placeholder="Search groups..." 
                  v-model="groupSearchQuery"
                  class="search-input-modal"
                />
              </div>
              <div class="groups-items">
                <div 
                  v-for="group in filteredMachineGroups" 
                  :key="group.id"
                  class="group-item"
                >
                  <input 
                    type="checkbox" 
                    :id="'edit-group-' + group.id"
                    :value="group.id"
                    v-model="editFormData.selectedGroups"
                    class="group-checkbox"
                  />
                  <label :for="'edit-group-' + group.id" class="group-label">
                    <div class="group-info">
                      <span class="group-name-text">{{ group.name }}</span>
                      <span class="group-date">Created {{ group.createdDate }}</span>
                    </div>
                    <span class="group-count">{{ group.machineCount }} machines</span>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="editTargetOption === 'all'" class="target-description">
              This ad will be pushed to all machines in the network
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="closeEditAdModal">Cancel</button>
          <button class="btn-push-ad" @click="saveEditedAd">Save Changes</button>
        </div>
      </div>
    </div>

    <!-- Target Assignments Modal -->
    <div v-if="showTargetAssignmentsModal" class="modal-overlay" @click.self="closeTargetAssignments">
      <div class="modal-content target-assignments-modal">
        <div class="modal-header">
          <div class="modal-title">
            <i class="el-icon-view modal-icon"></i>
            <span>Target Assignments</span>
          </div>
          <button class="modal-close-btn" @click="closeTargetAssignments">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <div class="modal-body" v-if="targetAssignmentAd">
          <div class="target-assignment-ad-info">
            <img v-if="targetAssignmentAd.type !== 1" :src="targetAssignmentAd.url || targetAssignmentAd.thum" :alt="targetAssignmentAd.head" class="target-assignment-thumb">
            <video
              v-else
              :src="targetAssignmentAd.url || targetAssignmentAd.thum"
              class="target-assignment-thumb target-assignment-thumb-video"
              muted
              loop
              playsinline
              preload="metadata"
            ></video>
            <div class="target-assignment-ad-text">
              <div class="target-assignment-title">{{ targetAssignmentAd.head }}</div>
              <div class="target-assignment-company">{{ targetAssignmentAd.advertiser }}</div>
            </div>
          </div>
          <div class="target-assignment-content">
            <template v-if="targetAssignmentDetails.target === 0">
              <div class="target-all-pill">All Machines</div>
              <p class="target-all-desc">This ad is pushed to all machines in the network</p>
            </template>
            <template v-else-if="targetAssignmentDetails.target === 1">
              <h4 class="target-section-heading">Machine Groups</h4>
              <div class="target-list-box">
                <div v-for="(grp, idx) in targetGroupDetails" :key="grp.groupId || idx" class="target-list-item">
                  <span class="target-item-name">{{ grp.groupName || grp.groupId }}</span>
                  <span class="target-item-meta">{{ grp.totalMachines != null ? grp.totalMachines : 0 }} machines</span>
                </div>
                <div v-if="targetGroupDetails.length === 0 && !targetGroupLoading" class="target-list-empty">No groups assigned</div>
                <div v-if="targetGroupLoading" class="target-list-empty">Loading...</div>
              </div>
            </template>
            <template v-else-if="targetAssignmentDetails.target === 2">
              <h4 class="target-section-heading">Machines</h4>
              <div class="target-list-box">
                <el-checkbox-group v-model="selectedTargetMachineRowIds">
                  <div v-for="(m, idx) in targetAssignmentDetails.machines" :key="idx" class="target-list-item target-list-item-machine">
                    <el-checkbox :label="m.rowId" :disabled="!m.rowId">
                      <div class="target-machine-row">
                        <span class="target-machine-label">Machine name:</span>
                        <span class="target-item-name">{{ m.equipmentName ? m.equipmentName + ' (' + (m.equipmentNo || '') + ')' : (m.equipmentNo || 'N/A') }}</span>
                      </div>
                      <div class="target-machine-row">
                        <span class="target-machine-label">Locale:</span>
                        <span class="target-item-meta">{{ m.address || 'N/A' }}</span>
                      </div>
                    </el-checkbox>
                  </div>
                </el-checkbox-group>
                <div v-if="targetAssignmentDetails.machines.length === 0" class="target-list-empty">No machines assigned</div>
              </div>
            </template>
            <template v-else>
              <p class="target-all-desc">—</p>
            </template>
          </div>
        </div>
        <div class="modal-footer">
          <button
            v-if="targetAssignmentDetails.target === 2"
            class="btn-push-ad"
            :disabled="selectedTargetMachineRowIds.length === 0"
            @click="removeSelectedMachinesFromAd"
          >Remove Selected ({{ selectedTargetMachineRowIds.length }})</button>
          <button class="btn-push-ad" @click="closeTargetAssignments">Close</button>
        </div>
      </div>
    </div>

    <!-- Upload Ad Modal -->
    <div v-if="showUploadAdModal" class="modal-overlay" @click.self="closeUploadAdModal">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title">
            <i class="el-icon-upload modal-icon"></i>
            <span>Upload Paid Ad</span>
          </div>
          <button class="modal-close-btn" @click="closeUploadAdModal">
            <i class="el-icon-close"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Drag & Drop Area -->
          <div 
            v-if="!uploadFormData.uploadedFileUrl"
            class="upload-drag-drop"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="handleFileDrop"
            :class="{ active: dragOver }"
          >
            <i class="el-icon-upload upload-icon"></i>
            <p class="upload-text">Drag & drop media files here or click to browse</p>
            <p class="upload-hint">Supports JPG, PNG, MP4, WebM</p>
            <input 
              type="file" 
              ref="fileInput" 
              class="file-input-hidden"
              accept=".jpg,.jpeg,.png,.gif,.mp4,.webm"
              @change="handleFileSelect"
            >
            <button 
              type="button" 
              class="browse-button"
              @click="triggerFileInput"
            >
              Browse Files
            </button>
          </div>

          <!-- Uploaded File Preview -->
          <div v-if="uploadFormData.uploadedFileUrl" class="uploaded-file-preview">
            <div class="preview-image-container">
              <img 
                v-if="uploadFormData.mediaType === 'image'" 
                :src="uploadFormData.uploadedFileUrl" 
                :alt="uploadFormData.title || 'Uploaded image'"
                class="preview-image"
              >
              <div v-else-if="uploadFormData.mediaType === 'video'" class="video-preview">
                <video 
                  :src="uploadFormData.uploadedFileUrl" 
                  class="preview-video"
                  controls
                  style="width: 100%; height: 150px; border-radius: 4px;"
                ></video>
              </div>
            </div>
            <div class="preview-info">
              <p class="preview-filename">
                <i class="el-icon-check-circle success-icon"></i>
                <strong v-if="uploadFormData.file">{{ uploadFormData.file.name }}</strong>
                <strong v-else>File uploaded</strong>
              </p>
              <p class="preview-url">
                <small>{{ uploadFormData.uploadedFileUrl.substring(0, 50) }}...</small>
              </p>
              <button 
                type="button" 
                class="clear-upload-btn"
                @click="clearUploadedFile"
              >
                <i class="el-icon-delete"></i>
                Clear
              </button>
            </div>
          </div>

          <!-- Title -->
          <div class="form-group">
            <label class="form-label">Title</label>
            <input 
              v-model="uploadFormData.title" 
              type="text" 
              placeholder="Ad title"
              class="form-input"
            >
          </div>

          <!-- Company -->
          <div class="form-group">
            <label class="form-label">Company</label>
            <input 
              v-model="uploadFormData.company" 
              type="text" 
              placeholder="Company name"
              class="form-input"
            >
          </div>

          <!-- Media Type -->
          <div class="form-group">
            <label class="form-label">Media Type</label>
            <select v-model="uploadFormData.mediaType" class="form-select">
              <option value="video">Video</option>
              <option value="image">Image</option>
            
            </select>
          </div>

          <!-- Placement Type -->
          <div class="form-group">
            <label class="form-label">Placement Type</label>
            <div class="placement-options">
              <button 
                type="button"
                class="placement-btn" 
                :class="{ active: uploadFormData.placementType === 'screen' }"
                @click="uploadFormData.placementType = 'screen'"
              >
                <i class="el-icon-monitor"></i>
                Screen
              </button>
              <button 
                type="button"
                class="placement-btn" 
                :class="{ active: uploadFormData.placementType === 'banner' }"
                @click="uploadFormData.placementType = 'banner'"
              >
                <i class="el-icon-picture"></i>
                Banner
              </button>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-cancel" @click="closeUploadAdModal">Cancel</button>
          <button class="btn-push-ad" @click="uploadAd">Upload</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/interface/index";
import url from "@/interface/url";
import methods from "@/interface/method";
import Cookies from "js-cookie";

export default {
  name: 'NetworkAds',
  data() {
    return {
      activeTab: 'campaigns',
      activeFilter: 'all',
      gallerySearchQuery: '',
      searchQuery: '',
      showPushAdModal: false,
      pushSelectedAdIds: [], // ad IDs selected for push (multi-select)
      showAdvertisementDropdown: false,
      pushDateRange: null,
      targetOption: 'all',
      showEditAdModal: false,
      showTargetAssignmentsModal: false,
      targetAssignmentAd: null,
      targetGroupDetails: [], // from getMachineGroup API when target=1
      targetGroupLoading: false,
      selectedTargetMachineRowIds: [], // user_equipment_ad row ids checked for removal, target=2 only
      editingAd: null,
      editFormData: {
        title: '',
        company: '',
        dateRange: null,
        target: 'all',
        selectedMachines: [],
        selectedGroups: []
      },
      editTargetOption: 'all',
      machineSearchQuery: '',
      groupSearchQuery: '',
      selectedMachines: [],
      selectedGroups: [],
      individualMachines: [],
      modalMachineList: [],
      machineListPage: { pageIndex: 1, pageSize: 20, pages: 1 },
      machineListLoading: false,
      machineListBackgroundLoading: false,
      machineSearchTimeout: null,
      currentMachineSearchName: '',
      machineGroups: [],
      availableMachines: [
        { id: 'VM-DT-003', name: 'VM-DT-003', location: '789 Pine St Corner' },
        { id: 'VM-AP-001', name: 'VM-AP-001', location: 'JFK Terminal 1 North' },
        { id: 'VM-AP-002', name: 'VM-AP-002', location: 'JFK Terminal 2 South' },
        { id: 'VM-AP-003', name: 'VM-AP-003', location: 'LaGuardia Terminal A' }
      ],
      showUploadAdModal: false,
      uploadFormData: {
        title: '',
        company: '',
        mediaType: 'video',
        placementType: 'screen',
        file: null,
        uploadedFileUrl: null, // URL of uploaded file from S3
        videoDuration: 30 // Auto-detected video duration
      },
      dragOver: false,
      loading: false,
      ads: [],
      galleryItems: [],
      campaignVideoThumbnail: {}, // ad.id -> data URL (first frame from video)
      pageIndex: 1,
      pageSize: 10,
      totalCount: 0,
      endDateDefaultTime: '23:59:59' // Default time for end date picker (11:59:59 PM)
    };
  },
  mounted() {
    // load gallery and campaigns separately
    this.fetchAds();
    this.fetchCampaignAds();
    this.fetchMachineGroups();
    // Individual machines are loaded on-demand via loadModalMachinesFirst (see targetOption watcher)
    // because fetching all at once doesn't scale past a few hundred machines.
  },
  watch: {
    activeTab(newVal) {
      if (newVal === 'campaigns') {
        this.fetchCampaignAds();
      } else if (newVal === 'gallery') {
        this.fetchAds();
      }
    },
    targetOption(newVal) {
      if (newVal === 'individual' && this.modalMachineList.length === 0 && !this.machineListLoading) {
        this.loadModalMachinesFirst();
      }
    },
    machineSearchQuery(newVal) {
      if (this.targetOption !== 'individual') return;
      if (this.machineSearchTimeout) clearTimeout(this.machineSearchTimeout);
      this.machineSearchTimeout = setTimeout(() => {
        this.loadModalMachinesFirst(newVal);
      }, 300);
    },
    filteredAds: {
      handler(ads) {
        const adIds = new Set((ads || []).map(a => a.adId != null ? a.adId : a.id));
        Object.keys(this.campaignVideoThumbnail).forEach(id => {
          if (!adIds.has(id)) this.$delete(this.campaignVideoThumbnail, id);
        });
        (ads || []).filter(ad => ad.type === 1 && ad.url).forEach(ad => {
          const key = ad.adId != null ? ad.adId : ad.id;
          if (!this.campaignVideoThumbnail[key]) this.captureVideoThumb(ad);
        });
      },
      immediate: true
    }
  },
  computed: {
    isAdmin() {
      return Cookies.get('roleId') === '0';
    },
    filteredAds() {
      if (!this.ads || this.ads.length === 0) return [];
      let filtered = this.ads;
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(ad =>
          (ad.head || '').toLowerCase().includes(q) ||
          (ad.advertiser || '').toLowerCase().includes(q)
        );
      }
      return filtered;
    },
    filteredMachines() {
      return this.availableMachines.filter(machine =>
        machine.name.toLowerCase().includes(this.machineSearchQuery.toLowerCase()) ||
        machine.location.toLowerCase().includes(this.machineSearchQuery.toLowerCase())
      );
    },
    filteredGalleryItems() {
      let filtered = this.galleryItems;

      // Filter by search query (title or company)
      if (this.gallerySearchQuery.trim()) {
        const query = this.gallerySearchQuery.toLowerCase();
        filtered = filtered.filter(item =>
          item.title.toLowerCase().includes(query) ||
          item.company.toLowerCase().includes(query)
        );
      }

      // Filter by display type (screen/banner)
      if (this.activeFilter === 'all') {
        return filtered;
      } else if (this.activeFilter === 'screen') {
        return filtered.filter(item => item.display === 1);
      } else if (this.activeFilter === 'banner') {
        return filtered.filter(item => item.display === 2);
      }
      return filtered;
    },
    filteredIndividualMachines() {
      if (!this.machineSearchQuery) {
        return this.individualMachines;
      }
      return this.individualMachines.filter(machine =>
        (machine.equipmentNo || '').toLowerCase().includes(this.machineSearchQuery.toLowerCase()) ||
        (machine.address || '').toLowerCase().includes(this.machineSearchQuery.toLowerCase()) ||
        (machine.group || '').toLowerCase().includes(this.machineSearchQuery.toLowerCase())
      );
    },
    modalMachinesFiltered() {
      if (!this.machineSearchQuery) return this.modalMachineList;
      const q = this.machineSearchQuery.toLowerCase();
      return this.modalMachineList.filter(m =>
        (m.equipmentNo || '').toLowerCase().includes(q) ||
        (m.address || '').toLowerCase().includes(q) ||
        (m.equipmentName || '').toLowerCase().includes(q) ||
        (m.group || '').toLowerCase().includes(q)
      );
    },
    targetAssignmentDetails() {
      const ad = this.targetAssignmentAd;
      if (!ad || !this.ads) return { target: null, machines: [], groups: [], groupIds: [] };
      const rows = this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id));
      const target = ad.target != null ? ad.target : (rows[0] && rows[0].target);
      const groupIds = [...new Set(rows.flatMap(r => {
        const g = r.groupId;
        if (Array.isArray(g)) return g.filter(Boolean);
        return g != null && g !== '' ? [g] : [];
      }))];
      // GroupedAvailableAdsDTO returns equipmentId and id as PARALLEL arrays
      // (equipmentId[i] <-> id[i] is the removable user_equipment_ad row for that
      // machine) — zip them by index rather than treating id as a single value.
      const rowIdByEquipmentId = {};
      const equipmentIds = [...new Set(rows.flatMap(r => {
        const eids = Array.isArray(r.equipmentId) ? r.equipmentId : (r.equipmentId != null && r.equipmentId !== '' ? [r.equipmentId] : []);
        const rids = Array.isArray(r.id) ? r.id : (r.id != null ? [r.id] : []);
        const validEids = [];
        eids.forEach((eid, i) => {
          if (eid != null && eid !== '') {
            validEids.push(eid);
            rowIdByEquipmentId[eid] = rids[i];
          }
        });
        return validEids;
      }))];
      const machineList = this.individualMachines && this.individualMachines.length > 0 ? this.individualMachines : this.modalMachineList;
      const machines = equipmentIds.map(eid => {
        const m = machineList.find(x => String(x.id) === String(eid)) || machineList.find(x => String(x.equipmentNo) === String(eid));
        if (!m) {
          return { equipmentNo: String(eid), equipmentName: 'N/A', address: 'N/A', rowId: rowIdByEquipmentId[eid] };
        }
        return {
          equipmentNo: (m.equipmentNo != null && m.equipmentNo !== '') ? m.equipmentNo : 'N/A',
          equipmentName: (m.equipmentName != null && m.equipmentName !== '') ? m.equipmentName : ((m.equipmentNo != null && m.equipmentNo !== '') ? m.equipmentNo : 'N/A'),
          address: (m.address != null && m.address !== '') ? m.address : 'N/A',
          rowId: rowIdByEquipmentId[eid]
        };
      });
      const groups = groupIds.map(gid => {
        const g = this.machineGroups.find(gr => gr.id === gid);
        return g ? { name: g.name, machineCount: g.machineCount } : { name: gid, machineCount: 0 };
      });
      return { target, machines, groups, groupIds };
    },
    filteredMachineGroups() {
      if (!this.groupSearchQuery) {
        return this.machineGroups;
      }
      return this.machineGroups.filter(group =>
        group.name.toLowerCase().includes(this.groupSearchQuery.toLowerCase())
      );
    },
    pushSelectedAdCount() {
      return (this.pushSelectedAdIds || []).length;
    }
  },
  methods: {
    /**
     * Returns 'active' if today falls within the ad's startTime–endTime range,
     * otherwise 'inactive'. Ads with no dates default to 'active'.
     */
    getAdStatus(ad) {
      if (!ad.startTime && !ad.endTime) return 'active';
      const now = new Date();
      const start = ad.startTime ? new Date(ad.startTime) : null;
      const end = ad.endTime ? new Date(ad.endTime) : null;
      if (start && now < start) return 'inactive';
      if (end && now > end) return 'inactive';
      return 'active';
    },
    targetLabel(target) {
      if (target === 0) return 'All';
      if (target === 1) return 'Group';
      if (target === 2) return 'Individual';
      return '—';
    },
    /**
     * Capture first frame from video URL as thumbnail for campaign preview.
     * If capture fails (CORS, error), UI falls back to showing the video with same dimensions.
     */
    captureVideoThumb(ad) {
      const key = ad && (ad.adId != null ? ad.adId : ad.id);
      if (!ad || !key || !ad.url || this.campaignVideoThumbnail[key]) return;
      const v = document.createElement('video');
      v.muted = true;
      v.playsInline = true;
      v.preload = 'metadata';
      v.crossOrigin = 'anonymous';
      const onDone = () => {
        v.src = '';
        v.load();
      };
      v.addEventListener('loadeddata', () => {
        v.currentTime = 0;
      });
      v.addEventListener('seeked', () => {
        try {
          const c = document.createElement('canvas');
          c.width = v.videoWidth;
          c.height = v.videoHeight;
          const ctx = c.getContext('2d');
          ctx.drawImage(v, 0, 0);
          const dataUrl = c.toDataURL('image/jpeg', 0.85);
          this.$set(this.campaignVideoThumbnail, key, dataUrl);
        } catch (e) {
          // Canvas tainted (CORS) or other – fallback to video in template
        }
        onDone();
      });
      v.addEventListener('error', onDone);
      v.src = ad.url;
      v.load();
    },
    /**
     * Fetch paid ads from ad/list for the Gallery tab only.
     * Populates this.galleryItems only. Campaign tab uses fetchCampaignAds() → this.ads.
     */
    fetchAds() {
      this.loading = true;
      request.request(url.Adverts.listAdvertByGroupId, methods.get, { adType: 1 })
        .then(response => {
          if (response.code === 0) {
            const items = response.data || [];

            // gallery tab only — mapped shape (id + adId for assignAds payload)
            this.galleryItems = items.map(item => ({
              id: item.id,
              adId: item.adId != null ? item.adId : item.id,
              image: item.url || item.thum,
              title: item.head,
              company: item.advertiser,
              type: item.type,
              display: item.display,
              format: item.type === 0 ? 'image' : 'video',
              date: item.createTime
            }));
          } else {
            this.$message.error(response.msg || 'Failed to load ads');
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching ads:', error);
          this.$message.error('Error fetching ads');
          this.loading = false;
        });
    },
    /**
     * Fetch campaign ads from userEquipmentAd.adsList (ads available for equipment)
     * Populates the campaigns table (this.ads) only — leaves galleryItems untouched.
     */
    fetchCampaignAds() {
      this.loading = true;
      request.request(url.userEquipmentAd.adsList, methods.get, { adType: 1 })
        .then(response => {
          if (response.code === 0) {
            const items = response.data || [];
            console.debug('[NetworkAds] fetchCampaignAds: total items from adsList=', items.length);
            // campaigns table — raw records (id, adId, equipmentId, target, startTime, endTime, pushedBy, type, display, url, head, etc.)
            this.ads = items;
            this.totalCount = items.length;
          } else {
            this.$message.error(response.msg || 'Failed to load campaign ads');
          }
          this.loading = false;
        })
        .catch(error => {
          console.error('Error fetching campaign ads:', error);
          this.$message.error('Error fetching campaign ads');
          this.loading = false;
        });
    },
    /**
     * Fetch machine groups from userEquipment/fetchGroupMachines
     * Response: list of { groupId, groupName, createdDate, updatedDate, machineCount }
     */
    fetchMachineGroups() {
      request.request(url.UserMachineGroup.fetchGroupMachines, methods.get, {})
        .then(response => {
          if (response.code === 0) {
            const groups = response.data || [];
            this.machineGroups = groups.map(item => ({
              id: item.groupId,
              name: item.groupName,
              createdDate: item.createdDate ? String(item.createdDate).slice(0, 10) : '',
              machineCount: item.machineCount != null ? item.machineCount : 0,
              selected: false
            }));
          } else {
            this.$message.error(response.msg || 'Failed to load machine groups');
          }
        })
        .catch(error => {
          console.error('Error fetching machine groups:', error);
          this.$message.error('Error fetching machine groups');
        });
    },
    /**
     * Fetch all individual machines from userEquipment/list API
     * Response: List<UserEquipmentDTO>
     *   - id            : user equipment id
     *   - equipmentNo   : machine number (displayed as machine ID)
     *   - equipmentName : machine name
     *   - locale        : full address
     *   - teamName      : group name the machine belongs to
     *   - mqState       : online status (0: offline, 1: online)
     *   - enableStatus  : enable state (0: off, 1: enabled)
     */
    fetchIndividualMachines() {
      request.request(url.UserMachine.userList, methods.get, {})
        .then(response => {
          if (response.code === 0) {
            const machines = response.data || [];
            this.individualMachines = machines.map(item => ({
              id: item.id,
              equipmentNo: item.equipmentNo,
              equipmentName: item.equipmentName != null ? item.equipmentName : '',
              address: item.locale != null ? item.locale : (item.equipmentName != null ? item.equipmentName : ''),
              group: item.teamName || '',
              selected: false
            }));
          } else {
            this.$message.error(response.msg || 'Failed to load machines');
          }
        })
        .catch(error => {
          console.error('Error fetching individual machines:', error);
          this.$message.error('Error fetching individual machines');
        });
    },
    /**
     * Map API record to unified machine shape.
     * Supports fetchMachineDetails response: { equipmentId, equipmentNum, equipmentName, locale }
     * and userEquipment/select shape: { id, equipmentNo, name, locale, teamName }.
     */
    mapSelectRecordToMachine(record) {
      const id = record.equipmentId != null ? record.equipmentId : record.id;
      const equipmentNo = record.equipmentNum != null ? record.equipmentNum : record.equipmentNo;
      const equipmentName = (record.equipmentName != null ? record.equipmentName : record.name) || equipmentNo || '';
      const address = record.locale != null && record.locale !== '' ? record.locale : '';
      const group = record.teamName != null ? record.teamName : '';
      return {
        id,
        equipmentNo: equipmentNo != null ? equipmentNo : '',
        equipmentName: equipmentName != null ? equipmentName : '',
        address,
        group,
        selected: false
      };
    },
    loadModalMachinesFirst(searchName) {
      const trimmed = (searchName || '').trim();
      this.currentMachineSearchName = trimmed;
      this.modalMachineList = [];
      this.machineListPage = { pageIndex: 1, pageSize: this.machineListPage.pageSize || 20, pages: 1 };
      this.machineListLoading = true;
      const params = {
        type: 0,
        pageSize: this.machineListPage.pageSize,
        pageIndex: 1,
        lang: 'en_US'
      };
      if (trimmed) params.searchName = trimmed;
      request.request(url.UserMachineGroup.choosePage, methods.get, params)
        .then(res => {
          if (res.code === 0 && res.data && res.data.records) {
            this.modalMachineList = res.data.records.map(r => this.mapSelectRecordToMachine(r));
            this.machineListPage.pages = parseInt(res.data.pages, 10) || 1;
            this.machineListPage.pageIndex = 1;
          }
        })
        .catch(err => {
          console.error('Failed to load machines:', err);
          this.$message && this.$message.error && this.$message.error('Failed to load machines');
        })
        .finally(() => {
          this.machineListLoading = false;
        });
    },
    loadMoreModalMachines() {
      if (this.machineListLoading) return;
      if (this.machineListPage.pageIndex >= this.machineListPage.pages) return;
      this.machineListLoading = true;
      const nextIndex = this.machineListPage.pageIndex + 1;
      const params = {
        type: 0,
        pageSize: this.machineListPage.pageSize,
        pageIndex: nextIndex,
        lang: 'en_US'
      };
      if (this.currentMachineSearchName) params.searchName = this.currentMachineSearchName;
      request.request(url.UserMachineGroup.choosePage, methods.get, params)
        .then(res => {
          if (res.code === 0 && res.data && res.data.records && res.data.records.length > 0) {
            const more = res.data.records.map(r => this.mapSelectRecordToMachine(r));
            this.modalMachineList = this.modalMachineList.concat(more);
            this.machineListPage.pages = parseInt(res.data.pages, 10) || this.machineListPage.pages;
            this.machineListPage.pageIndex = nextIndex;
          }
        })
        .catch(err => {
          console.error('Failed to load more machines:', err);
        })
        .finally(() => {
          this.machineListLoading = false;
        });
    },
    onMachineListScroll(e) {
      const el = e.target;
      if (!el || this.machineListLoading) return;
      const threshold = 80;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
        this.loadMoreModalMachines();
      }
    },
    /**
     * Fetch all equipment via optimized fetchMachineDetails API on load.
     * Populates both modalMachineList (Push/Edit modals + search) and individualMachines (View popup).
     */
    fetchAllMachinesOnLoad() {
      this.machineListBackgroundLoading = true;
      request.request(url.UserMachine.fetchMachineDetails, methods.get, {})
        .then(res => {
          if (res.code === 0 && res.data) {
            const list = Array.isArray(res.data) ? res.data : (res.data.records || []);
            const mapped = list.map(item => this.mapSelectRecordToMachine(item));
            this.modalMachineList = mapped;
            this.individualMachines = mapped;
            this.machineListPage.pages = 1;
            this.machineListPage.pageIndex = 1;
          }
        })
        .catch(() => {})
        .finally(() => {
          this.machineListBackgroundLoading = false;
        });
    },
    openPushAdModal() {
      this.showPushAdModal = true;
      this.pushSelectedAdIds = [];
      this.showAdvertisementDropdown = false;
      this.pushDateRange = null;
      this.targetOption = 'all';
    },
    closePushAdModal() {
      this.showPushAdModal = false;
      this.showAdvertisementDropdown = false;
    },
    isPushAdSelected(item) {
      const id = String(item.adId != null ? item.adId : item.id);
      return this.pushSelectedAdIds.indexOf(id) !== -1;
    },
    togglePushAdSelection(item) {
      const id = String(item.adId != null ? item.adId : item.id);
      const idx = this.pushSelectedAdIds.indexOf(id);
      if (idx === -1) {
        this.pushSelectedAdIds = [...this.pushSelectedAdIds, id];
      } else {
        this.pushSelectedAdIds = this.pushSelectedAdIds.filter(x => x !== id);
      }
    },
    pushAd() {
      if (!this.pushSelectedAdIds || this.pushSelectedAdIds.length === 0) {
        this.$message.error('Please select at least one ad');
        return;
      }

      if (!this.pushDateRange || this.pushDateRange.length !== 2) {
        this.$message.error('Please select a date range');
        return;
      }
      const startMs = new Date(this.pushDateRange[0]).getTime();
      const endMs = new Date(this.pushDateRange[1]).getTime();
      if (endMs < startMs) {
        this.$message.error('End date must be after start date');
        return;
      }

      // targetType: 0 = All, 1 = Machine Group, 2 = Individual Machines
      const targetTypeMap = { all: 0, groups: 1, individual: 2 };
      const targetType = targetTypeMap[this.targetOption];

      if (targetType === 1) {
        const selectedGroupIds = this.machineGroups
          .filter(g => g.selected)
          .map(g => g.id);
        if (selectedGroupIds.length === 0) {
          this.$message.error('Please select at least one machine group');
          return;
        }
      }

      if (targetType === 2) {
        const selectedMachineIds = this.modalMachineList
          .filter(m => m.selected)
          .map(m => m.id);
        if (selectedMachineIds.length === 0) {
          this.$message.error('Please select at least one machine');
          return;
        }
      }

      // POST userEquipmentAd/assignAds — payload: target 0=All, 1=Group, 2=Individual; adIds = ad id from campaign/gallery
      const data = {
        target: targetType,
        adIds: this.pushSelectedAdIds.map(id => String(id)),
        startTime: this.pushDateRange[0],
        endTime: this.pushDateRange[1]
      };
      if (targetType === 1) {
        data.groupId = this.machineGroups.filter(g => g.selected).map(g => g.id);
      }
      if (targetType === 2) {
        data.equipmentIds = this.modalMachineList.filter(m => m.selected).map(m => m.id);
      }

      request.request(url.userEquipmentAd.assignAds, methods.post, data)
        .then(response => {
          if (response.code === 0) {
            this.$message.success('Ad assigned to machines successfully!');
            this.closePushAdModal();
            this.refreshAds();
          } else {
            this.$message.error(response.msg || 'Failed to assign ad to machines');
          }
        })
        .catch(error => {
          console.error('Error assigning ad to machines:', error);
          this.$message.error('Error assigning ad to machines');
        });
    },
    openEditAdModal(ad) {
      this.editingAd = ad;
      const target = ad.target != null ? ad.target : 0;
      const targetOption = target === 0 ? 'all' : target === 1 ? 'groups' : 'individual';
      let selectedGroups = [];
      let selectedMachines = [];
      if (this.ads && (target === 1 || target === 2)) {
        const rows = this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id));
        if (target === 1) {
          selectedGroups = [...new Set(rows.flatMap(r => {
            // Backend may return group ids in groupId (string) or groupId (array) depending on grouping/parsing.
            const g = (Array.isArray(r.groupIdList) && r.groupIdList.length > 0) ? r.groupIdList : r.groupId;
            if (Array.isArray(g)) return g.filter(Boolean).map(String);
            return g != null && g !== '' ? [String(g)] : [];
          }))];
        } else if (target === 2) {
          selectedMachines = [...new Set(rows.flatMap(r => {
            // Backend often returns assigned equipment as equipmentIdList (array); fall back to equipmentId/equipmentIds.
            if (Array.isArray(r.equipmentIdList) && r.equipmentIdList.length > 0) return r.equipmentIdList.filter(Boolean).map(String);
            const e = r.equipmentId != null ? r.equipmentId : r.equipmentIds;
            if (Array.isArray(e)) return e.filter(Boolean).map(String);
            return e != null && e !== '' ? [String(e)] : [];
          }))];
        }
      }
      const startTimeStr = ad.startTime ? String(ad.startTime).trim() : '';
      const endTimeStr = ad.endTime ? String(ad.endTime).trim() : '';
      const startFormatted = startTimeStr ? (startTimeStr.length >= 19 ? startTimeStr : startTimeStr + ' 00:00:00') : '';
      const endFormatted = endTimeStr ? (endTimeStr.length >= 19 ? endTimeStr : endTimeStr + ' 23:59:59') : '';
      this.editFormData = {
        id: ad.id,
        title: ad.head,
        company: ad.advertiser,
        dateRange: startFormatted && endFormatted ? [startFormatted, endFormatted] : null,
        target: targetOption,
        selectedMachines,
        selectedGroups
      };
      this.editTargetOption = targetOption;
      this.machineSearchQuery = '';
      this.groupSearchQuery = '';
      this.showEditAdModal = true;
    },
    closeEditAdModal() {
      this.showEditAdModal = false;
      this.editingAd = null;
      this.editFormData = {
        title: '',
        company: '',
        dateRange: null,
        target: 'all',
        selectedMachines: [],
        selectedGroups: []
      };
    },
    openTargetAssignments(ad) {
      this.targetAssignmentAd = ad;
      this.showTargetAssignmentsModal = true;
      this.targetGroupDetails = [];
      this.selectedTargetMachineRowIds = [];
      const target = ad.target != null ? ad.target : 0;
      if (target === 1 && this.ads) {
        const rows = this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id));
        const groupIds = [...new Set(rows.flatMap(r => {
          const g = r.groupId;
          if (Array.isArray(g)) return g.filter(Boolean);
          return g != null && g !== '' ? [g] : [];
        }))];
        if (groupIds.length > 0) {
          this.fetchTargetGroupDetails(groupIds);
        }
      } else if (target === 2 && this.ads) {
        const rows = this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id));
        const equipmentIds = [...new Set(rows.flatMap(r => {
          const eids = Array.isArray(r.equipmentId) ? r.equipmentId : (r.equipmentId != null && r.equipmentId !== '' ? [r.equipmentId] : []);
          return eids.filter(e => e != null && e !== '');
        }))];
        if (equipmentIds.length > 0) {
          this.fetchTargetMachineDetails(equipmentIds);
        }
      }
    },
    /**
     * Fetch only the machines referenced by a target=2 ad's assignment rows (not all
     * ~8000 machines) so targetAssignmentDetails can resolve real names/serials instead
     * of falling back to N/A.
     */
    fetchTargetMachineDetails(equipmentIds) {
      request.request(url.UserMachine.fetchMachineDetails, methods.get, { ids: equipmentIds })
        .then(response => {
          if (response.code === 0) {
            const list = Array.isArray(response.data) ? response.data : (response.data && response.data.records) || [];
            this.individualMachines = list.map(item => this.mapSelectRecordToMachine(item));
          }
        })
        .catch(error => {
          console.error('Error fetching target machine details:', error);
        });
    },
    fetchTargetGroupDetails(groupIds) {
      this.targetGroupLoading = true;
      request.request(url.UserMachineGroup.getMachineGroup, methods.post, groupIds)
        .then(response => {
          if (response.code === 0 && Array.isArray(response.data)) {
            this.targetGroupDetails = response.data;
          } else {
            this.targetGroupDetails = [];
          }
        })
        .catch(error => {
          console.error('Error fetching machine group details:', error);
          this.targetGroupDetails = [];
        })
        .finally(() => {
          this.targetGroupLoading = false;
        });
    },
    closeTargetAssignments() {
      this.showTargetAssignmentsModal = false;
      this.targetAssignmentAd = null;
      this.targetGroupDetails = [];
      this.targetGroupLoading = false;
      this.selectedTargetMachineRowIds = [];
    },
    saveEditedAd() {
      if (!this.editFormData.title || !this.editFormData.company) {
        this.$message.error('Please fill in all required fields');
        return;
      }
      const targetMap = { all: 0, groups: 1, individual: 2 };
      const target = targetMap[this.editTargetOption];
      if (target === 1 && (!this.editFormData.selectedGroups || this.editFormData.selectedGroups.length === 0)) {
        this.$message.error('Please select at least one machine group');
        return;
      }
      if (target === 2 && (!this.editFormData.selectedMachines || this.editFormData.selectedMachines.length === 0)) {
        this.$message.error('Please select at least one machine');
        return;
      }
      if (!this.editFormData.dateRange || this.editFormData.dateRange.length !== 2) {
        this.$message.error('Please select start and end date');
        return;
      }
      const editStartMs = new Date(this.editFormData.dateRange[0]).getTime();
      const editEndMs = new Date(this.editFormData.dateRange[1]).getTime();
      if (editEndMs < editStartMs) {
        this.$message.error('End date must be after start date');
        return;
      }

      const ad = this.editingAd;
      const rows = this.ads ? this.ads.filter(r => (r.adId != null ? r.adId : r.id) === (ad.adId != null ? ad.adId : ad.id)) : [ad];
      const assignmentIds = rows.map(r => r.id);
      const assignmentId = assignmentIds.length > 0 ? assignmentIds[0] : ad.id;

      const flatStrIds = (arr) => {
        if (!Array.isArray(arr)) return [];
        return arr.flat(Infinity).map(x => (x != null && x !== '' ? String(x) : null)).filter(Boolean);
      };
      const payload = {
        assignmentId:ad.assignmentId,
        target,
        startTime: this.editFormData.dateRange[0],
        endTime: this.editFormData.dateRange[1],
        pushedBy: ad.pushedBy != null ? ad.pushedBy : '',
        adId: ad.adId != null ? ad.adId : ad.id,
        sort: ad.sort != null ? ad.sort : 1,
        head: this.editFormData.title,
        advertiser: this.editFormData.company,
        groupId: target === 1 ? flatStrIds(this.editFormData.selectedGroups) : [],
        equipmentId: target === 2 ? flatStrIds(this.editFormData.selectedMachines) : [],
        id: assignmentIds,
        uId: ad.uId != null ? ad.uId : (ad.uid != null ? ad.uid : '1')
      };

      request.request(url.userEquipmentAd.editPaidAd, methods.post, payload)
        .then(response => {
          if (response.code === 0) {
            this.$message.success('Ad updated successfully!');
            this.closeEditAdModal();
            this.refreshAds();
          } else {
            this.$message.error(response.msg || 'Failed to update ad');
          }
        })
        .catch(error => {
          console.error('Error updating ad:', error);
          this.$message.error('Error updating ad');
        });
    },
    openUploadAdModal() {
      console.log('openUploadAdModal called');
      this.showUploadAdModal = true;
      this.uploadFormData = {
        title: '',
        company: '',
        mediaType: 'video',
        placementType: 'screen',
        file: null,
        uploadedFileUrl: null,
        videoDuration: 30
      };
      this.dragOver = false;
      console.log('Modal opened, showUploadAdModal:', this.showUploadAdModal);
    },
    triggerFileInput() {
      console.log('triggerFileInput called');
      console.log('fileInput ref exists?', this.$refs.fileInput);
      if (this.$refs.fileInput) {
        console.log('Clicking file input');
        this.$refs.fileInput.click();
      } else {
        console.error('fileInput ref is not available');
      }
    },
    clearUploadedFile() {
      console.log('Clearing uploaded file');
      this.uploadFormData.uploadedFileUrl = null;
      this.uploadFormData.file = null;
      this.uploadFormData.videoDuration = 30;
      // Also clear file input
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    closeUploadAdModal() {
      this.showUploadAdModal = false;
      this.uploadFormData = {
        title: '',
        company: '',
        mediaType: 'video',
        placementType: 'screen',
        file: null,
        uploadedFileUrl: null,
        videoDuration: 30
      };
      this.dragOver = false;
    },
    handleFileDrop(event) {
      console.log('File drop event triggered');
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        console.log('File dropped:', files[0].name);
        this.handleFileValidationAndUpload(files[0]);
      }
      this.dragOver = false;
    },
    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length > 0) {
        console.log('File selected:', files[0].name, files[0].type, files[0].size);
        this.handleFileValidationAndUpload(files[0]);
      }
    },
    /**
     * Validate file format and automatically upload to S3
     * @param {File} file - The file to validate and upload
     */
    handleFileValidationAndUpload(file) {
      console.log('handleFileValidationAndUpload called with file:', file.name);
      // Define allowed file types and extensions
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/webm'];
      const allowedExtensions = /\.(jpg|jpeg|png|gif|mp4|webm)$/i;

      // Check file type
      console.log('File type:', file.type, 'Extension test:', allowedExtensions.test(file.name));
      if (!allowedTypes.includes(file.type) && !allowedExtensions.test(file.name)) {
        console.log('File validation failed - unsupported type');
        this.$message.error('Unsupported file type. Please upload JPG, PNG, MP4, or WebM files only');
        return;
      }
      console.log('File type validation passed');

      // Check file size (5MB for images, 100MB for videos)
      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file.name);
      const isVideo = /\.(mp4|webm)$/i.test(file.name);
      const maxImageSize = 5 * 1024 * 1024; // 5MB
      const maxVideoSize = 100 * 1024 * 1024; // 100MB

      console.log('isImage:', isImage, 'isVideo:', isVideo);

      if (isImage && file.size > maxImageSize) {
        console.log('Image validation failed - too large');
        this.$message.error(`Image size must not exceed 5MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }

      if (isVideo && file.size > maxVideoSize) {
        console.log('Video validation failed - too large');
        this.$message.error(`Video size must not exceed 100MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }

      // Store file reference
      this.uploadFormData.file = file;

      // Automatically detect media type
      if (isImage) {
        this.uploadFormData.mediaType = 'image';
      } else if (isVideo) {
        this.uploadFormData.mediaType = 'video';
      }

      console.log('File validation passed, starting upload. mediaType:', this.uploadFormData.mediaType);
      // Auto-upload file to S3
      this.uploadFileToS3(file);
    },
    /**
     * Upload file to S3 via backend API
     * @param {File} file - The file to upload
     */
    uploadFileToS3(file) {
      console.log('uploadFileToS3 called with file:', file.name);
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Show loading indicator using the component's loading state
      console.log('Showing loading message, about to upload to:', url.Upload.advert);
      this.loading = true;
      this.$message({
        message: 'Uploading file to server...',
        type: 'info',
        duration: 0
      });

      // Upload file
      console.log('Starting file upload with request.upload()');
      request.upload(url.Upload.advert, formData)
        .then(response => {
          console.log('Upload response received:', response);
          this.loading = false;

          if (response.code === 0) {
            console.log('Upload successful, response data:', response.data);
            const fileUrl = response.data;
            
            // Store the uploaded file URL
            this.uploadFormData.uploadedFileUrl = fileUrl;
            console.log('Stored uploadedFileUrl:', this.uploadFormData.uploadedFileUrl);
            
            // Show success message
            this.$message.success('File uploaded successfully!');
            
            // For videos, try to get duration from the uploaded URL
            if (this.uploadFormData.mediaType === 'video') {
              console.log('Getting video duration for:', fileUrl);
              this.getVideoDuration(fileUrl);
            }
          } else {
            console.log('Upload failed with code:', response.code, 'message:', response.msg);
            this.$message.error(response.msg || 'File upload failed');
            this.uploadFormData.file = null;
            this.uploadFormData.uploadedFileUrl = null;
          }
        })
        .catch(error => {
          this.loading = false;
          console.error('Error uploading file:', error);
          this.$message.error('Error uploading file to server');
          this.uploadFormData.file = null;
          this.uploadFormData.uploadedFileUrl = null;
        });
    },
    /**
     * Get video duration from URL
     * @param {String} videoUrl - The URL of the uploaded video
     */
    getVideoDuration(videoUrl) {
      const video = document.createElement('video');
      video.src = videoUrl;
      video.addEventListener('loadedmetadata', () => {
        const duration = Math.ceil(video.duration);
        this.uploadFormData.videoDuration = duration;
        console.log('Video duration detected:', duration, 'seconds');
      });
      video.addEventListener('error', () => {
        console.log('Could not detect video duration');
        this.uploadFormData.videoDuration = 30; // Default to 30 seconds
      });
    },
    /**
     * Refresh ads based on currently active tab.
     * If campaigns tab is active, call fetchCampaignAds(); otherwise call fetchAds().
     */
    refreshAds() {
      this.fetchCampaignAds();
      this.fetchAds();
    },
    uploadAd() {
      // Validate required fields
      if (!this.uploadFormData.title || !this.uploadFormData.company) {
        this.$message.error('Please fill in all required fields (Title and Company)');
        return;
      }

      // Check if file has been uploaded
      if (!this.uploadFormData.uploadedFileUrl) {
        this.$message.error('Please select and upload a file first');
        return;
      }

      // Prepare ad data with uploaded file URL
      const adData = {
        head: this.uploadFormData.title,
        advertiser: this.uploadFormData.company,
        url: this.uploadFormData.uploadedFileUrl,
        type: this.uploadFormData.mediaType === 'video' ? 1 : 0,
        display: this.uploadFormData.placementType === 'screen' ? 1 : 2,
        len: this.uploadFormData.videoDuration || (this.uploadFormData.mediaType === 'video' ? 30 : 15),
        thum: this.uploadFormData.uploadedFileUrl,
        adType: 1 // Paid ad type
      };

      // Create ad record with uploaded file URL
      request.request(url.Adverts.insert, methods.post, adData)
        .then(response => {
          if (response.code === 0) {
            this.$message.success('Ad created successfully!');
            this.closeUploadAdModal();
            this.refreshAds();
          } else {
            this.$message.error(response.msg || 'Failed to create ad');
          }
        })
        .catch(error => {
          console.error('Error creating ad:', error);
          this.$message.error('Error creating ad');
        });
    },
    deleteAd(ad) {
      // accept either the ad object (campaign row with adId) or an id primitive
      console.debug('[NetworkAds] deleteAd called with:', ad);
      let resolvedId = null;
      if (ad != null && typeof ad === 'object') {
        resolvedId = ad.assignmentId;
      } else if (ad != null && (typeof ad === 'string' || typeof ad === 'number')) {
        resolvedId = ad;
      }
      this.$confirm('Are you sure you want to delete this ad?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        if (resolvedId === null || resolvedId === undefined || resolvedId === '') {
          console.error('[NetworkAds] deleteAd: could not resolve ad id, aborting delete');
          this.$message.error('Could not determine ad id to delete');
          return;
        }
        // send adId as a query param to the delete endpoint so backends expecting params work reliably
        const endpoint = `${url.userEquipmentAd.removeAds}?assignmentId=${encodeURIComponent(resolvedId)}`;
        request.request(endpoint, methods.delete, {})
          .then(response => {
            if (response.code === 0) {
              this.$message.success('Ad deleted successfully!');
              this.refreshAds();
            } else {
              this.$message.error(response.msg || 'Failed to delete ad');
            }
          })
          .catch(error => {
            console.error('Error deleting ad:', error);
            this.$message.error('Error deleting ad');
          });
      }).catch(() => {
        // Cancelled
      });
    },
    removeSelectedMachinesFromAd() {
      if (this.selectedTargetMachineRowIds.length === 0) return;
      const count = this.selectedTargetMachineRowIds.length;
      this.$confirm(`Remove ${count} machine${count !== 1 ? 's' : ''} from this ad?`, 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        request.request(url.userEquipmentAd.removeAssignmentRows, methods.delete, this.selectedTargetMachineRowIds)
          .then(response => {
            if (response.code === 0) {
              this.$message.success('Machine(s) removed from ad');
              this.selectedTargetMachineRowIds = [];
              this.refreshAds();
            } else {
              this.$message.error(response.msg || 'Failed to remove machine(s)');
            }
          })
          .catch(error => {
            console.error('Error removing machine(s) from ad:', error);
            this.$message.error('Error removing machine(s)');
          });
      }).catch(() => {
        // Cancelled
      });
    },
    deleteGalleryItem(item) {
      this.$confirm('Are you sure you want to delete this item?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        const data = { id: item.id };
        request.request(url.Adverts.delete, methods.delete, data)
          .then(response => {
            if (response.code === 0) {
              this.$message.success('Item deleted successfully!');
              this.refreshAds();
            } else {
              this.$message.error(response.msg || 'Failed to delete item');
            }
          })
          .catch(error => {
            console.error('Error deleting item:', error);
            this.$message.error('Error deleting item');
          });
      }).catch(() => {
        // Cancelled
      });
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.push-paid-ads-container {
  width: stretch;
  background-color: var(--vtm-well);
  padding: 30px;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

/* Header Section */
.header-section {
  margin-bottom: 30px;
}

.badge-title {
  display: flex;
  align-items: center;
  gap: 15px;
}

.admin-badge {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  display: inline-block;
}

.header-section h1 {
  font-size: 32px;
  color: var(--vtm-ink);
  font-weight: 600;
}

/* Tab Navigation */
.tab-navigation {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}

.tab-button {
  background: none;
  border: none;
  padding: 12px 0;
  font-size: 15px;
  color: var(--vtm-ink-2);
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 3px solid transparent;
  transition: all 0.3s ease;
  font-family: inherit;
}



.tab-button.active {
  color: var(--vtm-ink);
 
}

.tab-icon {
  font-size: 18px;
}

/* Search and Action Bar */
.search-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;
}

.search-container {
  display: flex;
  gap: 10px;
  flex: 1;
  max-width: 500px;
}

.search-input {
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  flex: 1;
  padding: 12px 16px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
}

.search-input::placeholder {
  color: var(--vtm-ink-3);
}

.search-button,
.push-new-ad-button,
.upload-ad-button {
  padding: 12px 24px;
  background-color: var(--vtm-mint);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: inherit;
}

.search-button:hover,
.push-new-ad-button:hover,
.upload-ad-button:hover {
  background-color: var(--vtm-mint-dim);
}

.push-new-ad-button,
.upload-ad-button {
  white-space: nowrap;
}

/* Info Box */
.info-box {
  background: var(--vtm-info-soft);
  border: 1px solid var(--vtm-line-2);
  padding: 20px;
  margin-bottom: 30px;
  border-radius: 6px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.info-icon {
  font-size: 20px;
}

.info-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.info-description {
  color: var(--vtm-ink-2);
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
}

/* Table Section */
.table-container {
  background: var(--vtm-surface);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.ads-table {
  width: 100%;
  border-collapse: collapse;
}

.ads-table thead {
  background-color: var(--vtm-well);
}

.ads-table th {
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: var(--vtm-ink-2);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--vtm-line);
}

.ads-table tbody tr {
  border-bottom: 1px solid var(--vtm-line);
  transition: background-color 0.2s ease;
}

.ads-table tbody tr:hover {
  background-color: var(--vtm-surface-2);
}

.ads-table td {
  margin-top: 50px;
  padding: 16px;
  font-size: 14px;
  color: var(--vtm-ink);
}

.preview-cell {
  text-align: center;
}

.preview-image {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid var(--vtm-line);
  display: block;
  margin: 0 auto;
  vertical-align: middle;
}

.campaign-preview-video {
  display: block;
  object-fit: cover;
  margin: 0 auto;
}

.type-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #d0d0d0;
  background-color: #f9f9f9;
}

.type-badge.screen {
  border-color: #1976d2;
  color: #1976d2;
  background-color: #e3f2fd;
}

.type-badge.banner {
  border-color: #ff9800;
  color: #ff9800;
  background-color: #fff3e0;
}

.type-badge.image {
  border-color: #7b1fa2;
  color: #7b1fa2;
  background-color: #f3e5f5;
}

.type-badge.video {
  border-color: #ff6f00;
  color: #ff6f00;
  background-color: #fff3e0;
}

.view-badge,
.target-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--vtm-ink);
  font-size: 13px;
  padding: 2px 5px;
  font-weight: 700;
}
.view-badge:hover {
  background-color: var(--vtm-well);
  cursor: pointer;
  padding: 4px 5px;
  border-radius: 4px;
}

.eye-icon {
  font-size: 14px;
  margin-right: 4px;
}

.action-btn i {
  margin-right: 4px;
}

.filter-btn i {
  margin-right: 6px;
}

.delete-gallery-btn i {
  font-size: 16px;
}

.status-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background-color: #e3f2fd;
  color: #1976d2;
}

.status-badge.active {
  background-color: #c8e6c9;
  color: #2e7d32;
}

.status-badge.inactive {
  background-color: #fce4ec;
  color: #c62828;
}

.date-range-cell {
  white-space: nowrap;
  font-size: 13px;
}

.date-arrow {
  color: var(--vtm-ink-3);
  font-size: 11px;
}

.no-date {
  color: var(--vtm-ink-3);
  font-size: 12px;
}

.actions-cell {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
}

.edit-btn {
  background-color: var(--vtm-well);
  color: var(--vtm-ink);
  border: 0.5px solid var(--vtm-line-2);
}



.delete-btn {
  background-color: var(--vtm-crit);
  color: #ffffff;
}



/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 30px;
}

.total-count {
  color: var(--vtm-ink-3);
  font-size: 13px;
}

.pagination-controls {
  display: flex;
  gap: 10px;
}

.pagination-btn {
  padding: 8px 12px;
  border: 1px solid var(--vtm-line-2);
  background: var(--vtm-surface);
  color: var(--vtm-ink-2);
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.3s ease;
  font-family: inherit;
}

.pagination-btn:hover {
  border-color: var(--vtm-mint);
  color: var(--vtm-mint-ink);
}

.pagination-btn.active {
  background-color: var(--vtm-mint);
  color: white;
  border-color: var(--vtm-mint);
}

/* Gallery Section */
.gallery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  gap: 20px;
}

.filter-search {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.filter-buttons {
  display: flex;
  gap: 10px;
}

.filter-btn {
  padding: 8px 16px;
 
  background: var(--vtm-well);
  color: var(--vtm-ink-2);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
}



.filter-btn.active {
  
  color: var(--vtm-ink);
  
}

/* Gallery Grid */
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.gallery-card {
  background: var(--vtm-surface);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.gallery-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.gallery-image-container {
  position: relative;
  overflow: hidden;
  height: 200px;
  background: var(--vtm-well);
}

.gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gallery-type-label {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(25, 118, 210, 0.9);
  color: white;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.gallery-format-label {
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  color: #666;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.gallery-info {
  padding: 16px;
}

.gallery-info h3 {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin-bottom: 4px;
}

.gallery-info p {
  font-size: 12px;
  color: var(--vtm-ink-3);
  margin-bottom: 12px;
}

.gallery-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.gallery-date {
  font-size: 12px;
  color: var(--vtm-ink-3);
}

.delete-gallery-btn {
  background: none;
  border: none;
  color: var(--vtm-crit);
  cursor: pointer;
  font-size: 16px;
  padding: 4px 8px;
  transition: all 0.3s ease;
}

.delete-gallery-btn:hover {
  color: var(--vtm-crit);
  transform: scale(1.2);
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

/* Custom Dropdown Styles */
.custom-dropdown {
  position: relative;
  width: 100%;
}

.dropdown-toggle {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  background-color: var(--vtm-surface);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  font-family: inherit;
  text-align: left;
}

.dropdown-toggle:hover {
  border-color: var(--vtm-mint);
}

.dropdown-toggle:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

.selected-item {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.selected-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid var(--vtm-line);
  flex-shrink: 0;
}

.selected-thumbnail-video {
  object-fit: cover;
}

.selected-text {
  flex: 1;
  min-width: 0;
}

.selected-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-company {
  font-size: 12px;
  color: var(--vtm-ink-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.selected-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.selected-badge.banner {
  background-color: #fff3e0;
  color: #ff9800;
  border: 1px solid #ffe0b2;
}

.selected-badge.screen {
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.selected-badge.image {
  background-color: #f3e5f5;
  color: #7b1fa2;
  border: 1px solid #e1bee7;
}

.selected-badge.video {
  background-color: #fff3e0;
  color: #ff6f00;
  border: 1px solid #ffe0b2;
}

.placeholder-text {
  color: var(--vtm-ink-3);
  font-size: 14px;
}

.dropdown-arrow {
  color: var(--vtm-ink-2);
  font-size: 14px;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.dropdown-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: var(--vtm-surface);
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid var(--vtm-line);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: var(--vtm-surface-2);
}

.dropdown-item.active {
  background-color: var(--vtm-well);
}

.checkmark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: var(--vtm-mint-ink);
  font-weight: bold;
  flex-shrink: 0;
}

.checkmark i {
  font-size: 16px;
}

.dropdown-item-checkbox {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--vtm-mint);
}

.dropdown-list-multiselect .dropdown-item-checkbox {
  margin-right: 4px;
}

.dropdown-item-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid var(--vtm-line);
  flex-shrink: 0;
}

.dropdown-item-thumbnail-video {
  object-fit: cover;
}

.dropdown-item-content {
  flex: 1;
  min-width: 0;
}

.dropdown-item-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-item-company {
  font-size: 12px;
  color: var(--vtm-ink-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-item-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.dropdown-item-badge.banner {
  background-color: #fff3e0;
  color: #ff9800;
  border: 1px solid #ffe0b2;
}

.dropdown-item-badge.screen {
  background-color: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.dropdown-item-badge.image {
  background-color: #f3e5f5;
  color: #7b1fa2;
  border: 1px solid #e1bee7;
}

.dropdown-item-badge.video {
  background-color: #fff3e0;
  color: #ff6f00;
  border: 1px solid #ffe0b2;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--vtm-surface);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--vtm-line);
}

.modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.modal-icon {
  font-size: 24px;
  color: var(--vtm-mint-ink);
}

.modal-close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: var(--vtm-ink-3);
  cursor: pointer;
  padding: 0;
  transition: color 0.3s ease;
}

.modal-close-btn:hover {
  color: var(--vtm-ink);
}

.modal-body {
  padding: 24px;
}

/* Target Assignments modal */
.target-assignments-modal .modal-body {
  min-width: 360px;
}
.target-assignment-ad-info {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}
.target-assignment-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--vtm-line);
}

.target-assignment-thumb-video {
  object-fit: cover;
}

.target-assignment-ad-text {
  flex: 1;
}
.target-assignment-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin-bottom: 4px;
}
.target-assignment-company {
  font-size: 14px;
  color: var(--vtm-ink-2);
}
.target-assignment-content {
  margin-top: 16px;
}
.target-all-pill {
  display: inline-block;
  padding: 10px 20px;
  border-radius: 24px;
  background: var(--vtm-info-soft);
  color: var(--vtm-info);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
}
.target-all-desc {
  font-size: 14px;
  color: var(--vtm-ink-2);
  margin: 0 0 8px 0;
}
.target-section-heading {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin: 0 0 10px 0;
}
.target-list-box {
  background: var(--vtm-surface-2);
  border: 1px solid var(--vtm-line);
  border-radius: 8px;
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
}
.target-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  background: var(--vtm-surface);
  font-size: 14px;
}
.target-list-item:last-child {
  margin-bottom: 0;
}
.target-list-item-machine {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.target-machine-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}
.target-machine-label {
  color: var(--vtm-ink-2);
  font-size: 13px;
  min-width: 100px;
}
.target-item-name {
  font-weight: 600;
  color: var(--vtm-ink);
}
.target-item-meta {
  color: var(--vtm-ink-2);
  font-size: 13px;
}
.target-list-empty {
  padding: 12px;
  color: var(--vtm-ink-3);
  font-size: 14px;
  text-align: center;
}
.view-clickable {
  cursor: pointer;
}
.view-clickable:hover {
  text-decoration: underline;
}

.form-group {
  margin-bottom: 12px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin-bottom: 8px;
}

.form-select,
.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.3s ease;
}
.form-input {
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
}

.form-select:focus,
.form-input:focus {
  outline: none;
  border-color: var(--vtm-mint);
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
}

.form-select {
  background-color: var(--vtm-surface);
  cursor: pointer;
  color: var(--vtm-ink);
}

.form-select option {
  color: var(--vtm-ink);
}

.date-range-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.network-datetime-range-picker {
  max-width: 360px;
}

.date-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.date-icon {
  position: absolute;
  left: 12px;
  color: var(--vtm-mint-ink);
  font-size: 16px;
  pointer-events: none;
  z-index: 1;
}

.date-input {
  padding-left: 40px !important;
}

.date-input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  color: var(--vtm-mint-ink);
  opacity: 0.6;
}

.date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

.target-options {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.target-btn {
  flex: 1;
  padding: 12px 16px;
  
  background-color: var(--vtm-surface);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--vtm-ink-2);
  cursor: pointer;
  
  font-family: inherit;
}

.target-btn:hover {
  border: unset;
  color: var(--vtm-ink);
}

.target-btn.active {
  border: unset;
  color: var(--vtm-ink);
  font-weight: 600;
}

.target-description {
  font-size: 13px;
  color: var(--vtm-ink-2);
  padding: 12px;
  background-color: var(--vtm-well);
  border-radius: 6px;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--vtm-line);
  background-color: var(--vtm-surface-2);
}

.btn-cancel,
.btn-push-ad {
  padding: 12px 28px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.btn-cancel {
  background-color: var(--vtm-surface);
  color: var(--vtm-ink);
  border: 1px solid var(--vtm-line-2);
}

.btn-cancel:hover {
  background-color: var(--vtm-well);
  border-color: var(--vtm-line-2);
}

.btn-push-ad {
  background-color: var(--vtm-mint);
  color: white;
}

.btn-push-ad:hover {
  background-color: var(--vtm-mint-dim);
}

/* Edit Form Styles */
.edit-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 12px;
}

/* Machine Selection Styles */
.machine-selection {
  margin-top: 16px;
  border: 1px solid var(--vtm-line);
  border-radius: 6px;
  overflow: hidden;
}

.machines-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: var(--vtm-surface-2);
  border-bottom: 1px solid var(--vtm-line);
}

.search-machines {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
}

.search-machines:focus {
  outline: none;
  border-color: var(--vtm-mint);
}

.machines-selected {
  margin-left: 12px;
  color: var(--vtm-ink-3);
  font-size: 13px;
  white-space: nowrap;
}

.machines-items {
  max-height: 250px;
  overflow-y: auto;
}

.machines-items-scroll {
  max-height: 250px;
  overflow-y: auto;
}

.machines-list.machines-items-scroll {
  max-height: 250px;
  overflow-y: auto;
}

.machines-loading {
  padding: 12px;
  text-align: center;
  color: var(--vtm-ink-2);
  font-size: 13px;
}

.machine-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--vtm-line);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.machine-item:last-child {
  border-bottom: none;
}

.machine-item:hover {
  background-color: var(--vtm-surface-2);
}

.machine-checkbox {
  margin-top: 4px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.machine-label {
  flex: 1;
  cursor: pointer;
}

.machine-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}

.machine-id {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.machine-address {
  font-size: 12px;
  color: var(--vtm-ink-3);
}

.machine-group {
  font-size: 12px;
  color: var(--vtm-ink-2);
  font-weight: 500;
}

.machine-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin-bottom: 2px;
}

.machine-location {
  font-size: 12px;
  color: var(--vtm-ink-3);
}

/* Upload Modal Styles */
.upload-drag-drop {
  border: 2px dashed var(--vtm-line-2);
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  background-color: var(--vtm-surface-2);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 24px;
}

.upload-drag-drop:hover {
  border-color: var(--vtm-mint);
  background-color: var(--vtm-mint-soft);
}

.upload-drag-drop.active {
  border-color: var(--vtm-mint);
  background-color: var(--vtm-mint-soft);
}

.upload-icon {
  font-size: 48px;
  color: var(--vtm-ink-3);
  margin-bottom: 12px;
  display: block;
}

.upload-drag-drop.active .upload-icon {
  color: var(--vtm-mint-ink);
}

.upload-text {
  font-size: 16px;
  font-weight: 600;
  color: var(--vtm-ink);
  margin: 0 0 8px 0;
}

.upload-hint {
  font-size: 13px;
  color: var(--vtm-ink-3);
  margin: 0 0 16px 0;
}

.file-input-hidden {
  display: none;
}

.browse-button {
  padding: 10px 24px;
  background-color: var(--vtm-well);
  color: var(--vtm-ink);
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.browse-button:hover {
  background-color: var(--vtm-well);
  border-color: var(--vtm-mint);
}

/* Uploaded File Preview Styles */
.uploaded-file-preview {
  background-color: var(--vtm-surface-2);
  border: 1px solid var(--vtm-line);
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.preview-image-container {
  flex-shrink: 0;
}

.preview-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--vtm-line-2);
  background-color: var(--vtm-surface);
}

.video-preview {
  width: 120px;
  height: 120px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #000;
}

.preview-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
}

.preview-filename {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
  display: flex;
  align-items: center;
  gap: 8px;
}

.success-icon {
  color: var(--vtm-ok);
  font-size: 16px;
}

.preview-url {
  margin: 0;
  font-size: 12px;
  color: var(--vtm-ink-3);
  word-break: break-all;
}

.clear-upload-btn {
  align-self: flex-start;
  padding: 6px 12px;
  background-color: var(--vtm-surface);
  color: var(--vtm-crit);
  border: 1px solid var(--vtm-crit);
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
}

.clear-upload-btn:hover {
  background-color: var(--vtm-crit);
  color: #ffffff;
}

.placement-options {
  display: flex;
  gap: 12px;
}

.placement-btn {
  flex: 1;
  padding: 12px 16px;
  background-color: var(--vtm-surface);
  border: 1px solid var(--vtm-line-2);
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--vtm-ink-2);
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.placement-btn:hover {
  border-color: var(--vtm-mint);
  color: var(--vtm-mint-ink);
}

.placement-btn.active {
  background-color: var(--vtm-mint-soft);
  border-color: var(--vtm-mint);
  color: var(--vtm-mint-ink);
  font-weight: 600;
}

.placement-btn i {
  font-size: 16px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .push-paid-ads-container {
    padding: 20px;
  }

  .header-section h1 {
    font-size: 24px;
  }

  .search-action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-container {
    max-width: none;
  }

  .gallery-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  .ads-table {
    font-size: 12px;
  }

  .ads-table th,
  .ads-table td {
    padding: 12px 8px;
  }

  .actions-cell {
    flex-direction: column;
    gap: 5px;
  }
}

/* Groups List Styles */
.groups-list {
  margin-top: 16px;
  border: 1px solid var(--vtm-line);
  border-radius: 6px;
  overflow: hidden;
}

.search-box {
  padding: 12px 16px;
  background-color: var(--vtm-surface-2);
  border-bottom: 1px solid var(--vtm-line);
}

.search-input-modal {
  width: 95%;
  padding: 8px 12px;
  border: 1px solid var(--vtm-line-2);
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  box-sizing: border-box;
}

.search-input-modal:focus {
  outline: none;
  border-color: var(--vtm-mint);
}

.groups-items {
  max-height: 250px;
  overflow-y: auto;
}

.group-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--vtm-line);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.group-item:last-child {
  border-bottom: none;
}

.group-item:hover {
  background-color: var(--vtm-surface-2);
}

.group-checkbox {
  margin-top: 4px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

.group-label {
  flex: 1;
  cursor: pointer;
}

.group-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}

.group-name-text {
  font-size: 14px;
  font-weight: 600;
  color: var(--vtm-ink);
}

.group-date {
  font-size: 12px;
  color: var(--vtm-ink-3);
}

.group-count {
  font-size: 12px;
  color: var(--vtm-ink-2);
  font-weight: 500;
}

/* 暗色：类型/状态徽章原本是浅色底，改为半透明色调 */
[data-theme="dark"] .admin-badge { background: rgba(100, 181, 246, 0.14); color: #64b5f6; }
[data-theme="dark"] .type-badge { border-color: var(--vtm-line-2); background-color: var(--vtm-well); }
[data-theme="dark"] .type-badge.screen,
[data-theme="dark"] .selected-badge.screen,
[data-theme="dark"] .dropdown-item-badge.screen { background-color: rgba(100, 181, 246, 0.14); border-color: rgba(100, 181, 246, 0.5); color: #64b5f6; }
[data-theme="dark"] .type-badge.banner,
[data-theme="dark"] .type-badge.video,
[data-theme="dark"] .selected-badge.banner,
[data-theme="dark"] .selected-badge.video,
[data-theme="dark"] .dropdown-item-badge.banner,
[data-theme="dark"] .dropdown-item-badge.video { background-color: var(--vtm-warn-soft); border-color: rgba(229, 160, 76, 0.5); color: var(--vtm-warn); }
[data-theme="dark"] .type-badge.image,
[data-theme="dark"] .selected-badge.image,
[data-theme="dark"] .dropdown-item-badge.image { background-color: rgba(206, 147, 216, 0.14); border-color: rgba(206, 147, 216, 0.5); color: #ce93d8; }
[data-theme="dark"] .status-badge { background-color: var(--vtm-info-soft); color: var(--vtm-info); }
[data-theme="dark"] .status-badge.active { background-color: var(--vtm-ok-soft); color: var(--vtm-ok); }
[data-theme="dark"] .status-badge.inactive { background-color: var(--vtm-crit-soft); color: var(--vtm-crit); }
</style>


